/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ErrorResponseModel } from '../../../../../core/models';
import { Injectable } from '@angular/core';
import { Layer4ApiService, Layer7ApiService, ServicesApiService } from '../../http';
import { LoadingService, LogService, ModalService, NotifyService, UtilsService } from '../../../../../core/services';
import { Observable } from 'rxjs';
import { StatusEnum } from '../../enums';

@Injectable()
export class ServicesUtilsService {
    constructor(
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private servicesAPI: ServicesApiService,
        private utilsService: UtilsService
    ) {
    }

    /**
     * getStatusEnums
     */
    get statusEnum(): any {
        return StatusEnum;
    }

    /**
     * Displays button based on status and button name.
     * @param status
     * @param enumStatus
     */
    public showButton(status: string, enumStatus: any): boolean {
        return status === enumStatus || status === this.statusEnum.DOWN || status === 'pending';
    }

    /**
     * Send a restart request via the service
     */
    public restartHaProxy(): void {
        this.notifyService.warning('Restarting service. Please wait...', { system: true, id: 'restartHapProxy' });
        this.servicesAPI.restartService('haproxy')
            .subscribe(
                () => {
                }
            )
            .add(() => {
                this.loadingService.loadingActionDotsEmitter.next(false);
                this.notifyService.removeNotificationById('restartHapProxy');
            });
    }

    /**
     * Perform an action on the supplied service e.g. online, drain, halt.
     * @param action
     * @param service
     * @param options
     */
    public actionService(action: string, service: any, options: any): void {
        const serviceName = service.serviceName || service.name;
        const { title, serviceType } = this.getActionTypeAndTitle(action);

        const confirmationObject = {
            title: `services_${title}`,
            message: `Are you sure that you want to ${title} ${this.formatServiceType(serviceType)}: ${serviceName}?`,
            options: { translateText: false },
            callBack: this.makeApiCall.bind(this, title, serviceType, service, options)
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Format service type string.
     * @param serviceType
     * @private
     */
    private formatServiceType(serviceType: string): string {
        let formattedType: string;
        switch (serviceType) {
            case 'Frontend':
                formattedType = 'frontend';
                break;
            case 'Backend':
                formattedType = 'backend';
                break;
            case 'RealServer':
                formattedType = 'real server';
                break;
            default:
                formattedType = '';
                break;
        }

        return formattedType;
    }

    /**
     * Make the call to API service.
     * @param title
     * @param serviceType
     * @param service
     * @param options
     * @private
     */
    private makeApiCall(title: string, serviceType: string, service: any, options: any): Observable<any> {
        return new Observable((observer): void => {
            const apiAction: string = title + serviceType;
            const serviceId: string = service.id;
            const apiService: any = this.getApiService(options.templateType);
            apiService[apiAction](serviceId)
                .subscribe({
                    next: (): void => {
                        options.onCompleteCallback();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        // Recreating the ID is to stop duplicate notifications
                        const serviceName: string = service.serviceName || service.name;
                        const template: 'ipvsDirector' | 'haproxy' = options.templateType === 'layer4' ? 'ipvsDirector' : 'haproxy';
                        this.notifyService.error(
                            `${serviceName} ${title} ${serviceType.toLowerCase()} failed. ${error.message}`,
                            { id: `${template}-${serviceType.toLowerCase() + 's'}-${serviceId}-${title}-put` }
                        );
                        observer.error(error);
                        observer.complete();
                    }
                })
                .add((): void => this.loadingService.loadingButtonEmitter.next(false));
        });
    }

    /**
     * Get Api Service.
     * @param templateType
     * @private
     */
    private getApiService(templateType: string): any {
        switch (templateType) {
            case 'layer4':
                return this.layer4ApiService;
            case 'layer7':
            case 'monitor':
                return this.layer7ApiService;
        }
    }

    /**
     * Split the action provided into title and service type.
     * @param action
     * @private
     */
    public getActionTypeAndTitle(action: string): any {
        const split = action.split('-');

        return {
            title: split[0],
            serviceType: split[1]
        };
    }

    /**
     * Error helper function.
     * @param error
     * @param title
     * @param component
     */
    public errorHelper(error: string, title: string, component: string) {
        const htmlDebugMessage = this.utilsService.formatCapturedError(error);
        this.logService.error(`${component}: ${title}`, error);
        this.notifyService.error(htmlDebugMessage, { static: true });
    }
}
